import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission, SessionUser } from '@workforce/shared';
import { api, ApiError } from './api';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return (await api.get<{ user: SessionUser }>('/auth/me')).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    const onUnauthorized = () => qc.setQueryData(['auth', 'me'], null);
    const onPwd = () => void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    window.addEventListener('wf:unauthorized', onUnauthorized);
    window.addEventListener('wf:password-change', onPwd);
    return () => {
      window.removeEventListener('wf:unauthorized', onUnauthorized);
      window.removeEventListener('wf:password-change', onPwd);
    };
  }, [qc]);

  const user = q.data ?? null;
  const value: AuthState = {
    user,
    loading: q.isLoading,
    can: (p) => !!user?.permissions.includes(p),
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    logout: async () => {
      // Stop notifications for this device, so the next person using it doesn't get them.
      try {
        const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
        const sub = await reg?.pushManager?.getSubscription();
        if (sub) {
          await api.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
          await sub.unsubscribe().catch(() => undefined);
        }
      } catch {
        /* notifications not supported */
      }
      await api.post('/auth/logout').catch(() => undefined);
      qc.clear();
      qc.setQueryData(['auth', 'me'], null);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
